import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Auto-save hook - saves data to localStorage with debounce
 */
export function useAutoSave<T>(
  key: string,
  data: T,
  interval: number = 3000 // 3 seconds
) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<string>('');
  const isInitialMount = useRef(true);
  const dataRef = useRef(data);

  // Keep ref synced with latest data
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Debounced auto-save effect
  useEffect(() => {
    // Skip first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const saveData = (currentData: T) => {
      const serialized = JSON.stringify(currentData);
      
      // Only save if data has actually changed
      if (serialized !== lastSavedRef.current) {
        try {
          localStorage.setItem(key, serialized);
          lastSavedRef.current = serialized;
          console.log(`💾 Auto-saved ${key}`);
        } catch (error) {
          console.error('Failed to auto-save:', error);
          toast.error('Auto-Save fehlgeschlagen');
        }
      }
    };

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Schedule new save
    timeoutRef.current = setTimeout(() => {
      saveData(data);
    }, interval);

    // On cleanup (re-render), just clear the timeout
    // We do NOT save here, otherwise we lose the "debounce" benefit
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [key, data, interval]);

  // Unmount effect - Force save on unmount
  useEffect(() => {
    return () => {
      // On unmount, if there's pending data (or just to be safe), save the latest data
      // We check against lastSavedRef inside the logic to avoid redundant writes
      const currentData = dataRef.current;
      const serialized = JSON.stringify(currentData);
      
      if (serialized !== lastSavedRef.current && !isInitialMount.current) {
        try {
          console.log(`💾 Force-saving on unmount: ${key}`);
          localStorage.setItem(key, serialized);
          lastSavedRef.current = serialized;
        } catch (error) {
          console.error('Failed to save on unmount:', error);
        }
      }
    };
  }, [key]); // Only run on mount/unmount (and key change)
}

/**
 * Load saved data from localStorage
 */
export function loadSavedData<T>(key: string, defaultValue: T): T {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      // Silent load - don't spam console
      return JSON.parse(saved) as T;
    }
  } catch (error) {
    console.error('Failed to load saved data:', error);
    // Clear corrupted data
    try {
      localStorage.removeItem(key);
      console.log(`🗑️ Cleared corrupted data for ${key}`);
    } catch (clearError) {
      console.error('Failed to clear corrupted data:', clearError);
    }
  }
  return defaultValue;
}

/**
 * Clear saved data from localStorage
 */
export function clearSavedData(key: string) {
  try {
    localStorage.removeItem(key);
    console.log(`🗑️ Cleared saved data for ${key}`);
  } catch (error) {
    console.error('Failed to clear saved data:', error);
  }
}